import type React from "react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { RootState } from "@/store";
import {
  resetPassword,
  selectAuthError,
  selectAuthLoading,
} from "@/store/slices/authSlice";
import { AnyAction, ThunkDispatch } from "@reduxjs/toolkit";
import {
  CheckCircle,
  ChevronLeft,
  CircleAlert,
  Eye,
  EyeOff,
  Loader2,
} from "lucide-react";
import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router";

export default function CreatePasswordForm() {
  const [formData, setFormData] = useState({
    password: "",
    confirmPassword: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const dispatch = useDispatch<ThunkDispatch<RootState, unknown, AnyAction>>();
  const navigate = useNavigate();
  const location = useLocation();

  // Get auth state from Redux
  const isLoading = useSelector(selectAuthLoading);
  const error = useSelector(selectAuthError);

  // Extract token from URL if present (for password reset verification)
  const queryParams = new URLSearchParams(location.search);
  const token = queryParams.get("token") || "";

  const passwordRequirements = [
    { text: "At least 8 characters", met: formData.password.length >= 8 },
    { text: "Contains uppercase letter", met: /[A-Z]/.test(formData.password) },
    { text: "Contains lowercase letter", met: /[a-z]/.test(formData.password) },
    { text: "Contains number", met: /\d/.test(formData.password) },
    {
      text: "Contains special character",
      met: /[!@#$%^&*(),.?":{}|<>]/.test(formData.password),
    },
  ];

  const isPasswordValid = passwordRequirements.every((req) => req.met);
  const passwordsMatch =
    formData.password === formData.confirmPassword &&
    formData.confirmPassword.length > 0;

  const handleInputChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!isPasswordValid) {
      // Don't use setError directly as we're using Redux for error state
      return;
    }

    if (!passwordsMatch) {
      // Don't use setError directly as we're using Redux for error state
      return;
    }

    if (!token) {
      console.error("Missing token parameter in URL");
      return;
    }

    try {
      // Dispatch reset password action with proper type casting
      const resultAction = await dispatch(
        resetPassword({
          token,
          password: formData.password,
        }) as unknown as AnyAction
      );

      // Check if the action was fulfilled
      if (
        resultAction.meta &&
        resultAction.meta.requestStatus === "fulfilled"
      ) {
        // Set success state
        setIsSuccess(true);

        // Redirect to signin after 3 seconds
        setTimeout(() => {
          navigate("/auth/signin");
        }, 3000);
      }
    } catch (error) {
      console.error("Password reset error:", error);
    }
  };

  if (isSuccess) {
    return (
      <div className="bg-white rounded-2xl p-8 w-full max-w-md shadow-2xl">
        <div className="text-center space-y-4">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
            <CheckCircle className="w-8 h-8 text-green-600" />
          </div>
          <div>
            <h3 className="text-2xl font-bold text-gray-900 mb-2">
              Password Reset!
            </h3>
            <p className="text-gray-600 text-sm">
              Your password has been successfully reset. You can now sign in
              with your new password.
            </p>
          </div>
          <div className="text-center">
            <div className="w-8 h-8 border-4 border-[#8887c1] border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
            <p className="text-sm text-gray-500">Redirecting to sign in...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-3xl p-[18px] w-full max-w-md shadow-2xl">
      <div className="mb-[2.5px] relative z-50">
        <button
          type="button"
          onClick={() => navigate("/auth/signin?type=signin")}
          className="text-[#1E1E1E] text-[14px] font-[500] no-underline hover:underline cursor-pointer flex items-center before:text-lg"
        >
          <ChevronLeft />
          Go back
        </button>
      </div>

      <div className="space-y-6 pb-[40px] p-[10px]">
        <h3 className="text-xl font-bold text-center text-gray-900">
          Create Password
        </h3>

        <form onSubmit={handleSubmit} className="space-y-8 mt-5">
          <div>
            <label
              htmlFor="new-password"
              className="block text-md font-medium text-gray-700 mb-2"
            >
              Create new password
            </label>
            <div className="relative">
              <Input
                id="new-password"
                type={showPassword ? "text" : "password"}
                autoComplete="new-password"
                placeholder="Your password"
                className="w-full h-12 rounded-xl border-gray-200 focus:outline-none focus:border-[#7872C8] focus:ring-[#7872C8] pr-12"
                value={formData.password}
                onChange={(e) => handleInputChange("password", e.target.value)}
                required
              />

              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showPassword ? (
                  <EyeOff className="w-4 h-4" />
                ) : (
                  <Eye className="w-4 h-4" />
                )}
              </button>
            </div>
          </div>
          <div>
            <label
              htmlFor="confirm-password"
              className="block text-md font-medium text-gray-700 mb-2"
            >
              Confirm new password
            </label>
            <div className="relative">
              <Input
                id="confirm-password"
                type={showConfirmPassword ? "text" : "password"}
                autoComplete="confirm-password"
                placeholder="Your password"
                className="w-full h-12 rounded-xl border-gray-200 focus:outline-none focus:border-[#7872C8] focus:ring-[#7872C8] pr-12"
                value={formData.confirmPassword}
                onChange={(e) =>
                  handleInputChange("confirmPassword", e.target.value)
                }
                required
              />

              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-3  top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showConfirmPassword ? (
                  <EyeOff className="w-4 h-4" />
                ) : (
                  <Eye className="w-4 h-4" />
                )}
              </button>
            </div>
          </div>

          {error && (
            <div className="w-full bg-[#FFE9EB] p-2 rounded-lg">
              <div className="w-full h-full border-l-4 pl-3 border-[#FF152C] flex flex-row items-center justify-start gap-3 rounded-lg">
                <div className="w-[24px] h-[24px]">
                  <CircleAlert className="w-[24px] h-[24px] text-[#FF152C]" />
                </div>
                <span className="text-sm font-semibold text-gray-700">
                  {error}
                </span>
              </div>
            </div>
          )}

          <div>
            {/* Password Requirements */}
            {formData.password && (
              <div className="space-y-2">
                <p className="text-md font-medium text-gray-700">
                  Password Requirements:
                </p>
                <div className="space-y-1">
                  {passwordRequirements.map((req, index) => (
                    <div
                      key={index}
                      className="flex items-center gap-2 text-xs"
                    >
                      <div
                        className={`w-2 h-2 rounded-full ${
                          req.met ? "bg-green-500" : "bg-gray-300"
                        }`}
                      />
                      <span
                        className={req.met ? "text-green-600" : "text-gray-500"}
                      >
                        {req.text}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Password Match Indicator */}
            {formData.confirmPassword && (
              <div className="flex items-center gap-2 text-xs mt-1">
                <div
                  className={`w-2 h-2 rounded-full ${
                    passwordsMatch ? "bg-green-500" : "bg-red-500"
                  }`}
                />
                <span
                  className={passwordsMatch ? "text-green-600" : "text-red-500"}
                >
                  {passwordsMatch
                    ? "Passwords match"
                    : "Passwords do not match"}
                </span>
              </div>
            )}
          </div>

          <Button
            type="submit"
            className="w-full mt-4 h-12 bg-[#FF6B6B] hover:bg-[#FF5252] text-white font-semibold rounded-xl text-lg shadow-lg hover:shadow-xl transition-all duration-200"
            disabled={isLoading || !isPasswordValid || !passwordsMatch}
          >
            {isLoading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Resetting Password...
              </>
            ) : (
              "Set Password"
            )}
          </Button>
        </form>
      </div>
    </div>
  );
}
